import { useEffect, useRef } from 'react';

const UNINITIALIZED = Symbol('UNINITIALIZED');

/**
 * Creates a constant value over the lifecycle of a component.
 *
 * Even if `useMemo` is provided an empty array as its final argument, it doesn't offer
 * a guarantee that it won't re-run for performance reasons later on. By using `useConstant`
 * you can ensure that initialisers don't execute twice or more.
 */
export function useConstant<T>(init: () => T) {
  const ref = useRef<T | typeof UNINITIALIZED>(UNINITIALIZED);

  if (ref.current === UNINITIALIZED) {
    ref.current = init();
  }

  return ref.current as T;
}

/**
 * Access the previous value of a given value. Tracked via ref, the value is
 * `null` until after the first render.
 *
 * Useful for comparing a prop value to its previous value during a render.
 */
export function usePrevious<T>(value: T) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    ref.current = value;
    return () => {
      ref.current = null;
    };
  }, [value]);

  return ref.current;
}
